import { ObjectMapper } from 'json-object-mapper';
import { PreviewForRenewalResponse } from '../models/preview-for-renewal-response.model';

function readDurationFromJson(payload: unknown): number | undefined {
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  const d = (payload as { duration?: unknown }).duration;
  return typeof d === 'number' ? d : undefined;
}

export function deserializeRenewalPreviewRow(
  item: unknown
): PreviewForRenewalResponse {
  const row = ObjectMapper.deserialize(PreviewForRenewalResponse, item);
  const duration = readDurationFromJson(item);
  if (duration !== undefined) {
    row.duration = duration;
  }
  return row;
}

/** HTTP payload: always an array of per-duration previews; legacy single object supported. */
export function deserializeRenewalPreviewPayload(
  raw: unknown
): PreviewForRenewalResponse[] {
  if (Array.isArray(raw)) {
    const slices = raw.map((item) => deserializeRenewalPreviewRow(item));
    return [...slices].sort(
      (a, b) => (a.duration ?? 0) - (b.duration ?? 0)
    );
  }
  return [deserializeRenewalPreviewRow(raw)];
}

/**
 * Stepper UI storage stores `previewResponse` as an array of per-duration rows.
 * Older clients persisted a single object; that shape is incompatible with list
 * handling and must be cleared so the price step refetches preview from the API.
 */
export function normalizeRenewalPreviewResponseFromUiStorage(raw: unknown): {
  value: PreviewForRenewalResponse[] | null;
  wasLegacyNonArray: boolean;
} {
  if (raw == null) {
    return { value: null, wasLegacyNonArray: false };
  }
  if (Array.isArray(raw)) {
    return { value: raw as PreviewForRenewalResponse[], wasLegacyNonArray: false };
  }
  return { value: null, wasLegacyNonArray: true };
}

/** Restores list after localStorage / JSON (plain objects → class instances). */
export function reviveRenewalPreviewListFromCache(
  parsed: unknown
): PreviewForRenewalResponse[] {
  if (!parsed) {
    return [];
  }
  if (Array.isArray(parsed)) {
    return parsed.map((item) =>
      item instanceof PreviewForRenewalResponse
        ? item
        : deserializeRenewalPreviewRow(item)
    );
  }
  return [deserializeRenewalPreviewRow(parsed)];
}
